import ReactGrid, { Table } from "@devexpress/dx-react-grid-bootstrap4";
import {
  faCalendar,
  faCreditCard,
  faFileAlt,
  faPaperPlane,
  faTimesCircle,
} from "@fortawesome/free-regular-svg-icons";
import {
  faCheck,
  faEnvelope,
  faPencilAlt,
  faRedoAlt,
  faUserCheck,
  faUserXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AxiosError } from "axios";
import { differenceInMinutes, format, isAfter, isBefore, parseISO } from "date-fns";
import React, { useCallback, useMemo, useState } from "react";
import { Button, ButtonProps, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory } from "react-router";
import styled from "styled-components";
import SweetAlert from "sweetalert2";
import { useRoleChecker } from "../../components/authorization-verifier";
import { useKeycloakAuthz } from "../../components/keycloak-provider";
import { usePropertyConfiguration } from "../../components/property-configuration-provider";
import { usePropertyLocales } from "../../components/property-locale-provider";
import { useProperty } from "../../components/property-provider";
import { useUserProfile } from "../../components/user-profile-provider";
import { useUserPropertyTickers } from "../../components/user-tickers-providers";
import { useAuthorizer } from "../../lib/action-authorizer";
import authorizeActions from "../../lib/actions";
import { getPropertyRpt, useUserAuthInfo } from "../../lib/auth";
import { useConfig } from "../../lib/config";
import { FilterValues } from "../../lib/filterPills";
import { useApiClient } from "../../lib/http";
import { CALL_CENTER_LIGHT_USER, CALL_CENTER_USER } from "../../lib/roles";
import { createFileDownloadFromRawData } from "../../lib/supportFunctions";
import { InsertReservationCallCenter } from "../dreamplaceInsertReservationCallCenter/InsertReservationCallCenter";
import { CustomDateTimeRenderer } from "../historicalReservation/renders";
import { getEntity } from "../propertyInfo/infrastructure/property.api";
import { Api, PaymentsApi } from "../reservationDetail/apis";
import { TableRowActionProps } from "../shared/actions";
import { FEATURE_TAG_MESSAGES } from "../shared/featureTags.messages";
import {
  dateFilter,
  inputFilter,
  selectFilter,
  selectorFilter,
} from "../shared/crud/filter-widgets";
import {
  default as CrudList,
  ExportButton,
  StyledTableRow,
  TableColumns,
  TableFilters,
} from "../shared/crud/list";
import { RendererProps } from "../shared/renderers";
import { BooleanRenderer, mapRenderer, StringRenderer } from "../shared/renderers/table";
import { OrderItemCodeVo } from "../store/orders/domain/value-objects/OrderItemCode";
import { StyledSpinner } from "../witadmin/CRUD/EntityForm/EntityForm";
import { ActivateListIcon } from "../witadmin/icons/ActivateListIcon";
import { Reservation, ReservationProps } from "./domain/Reservation";
import { ReservationApi } from "./insfrastructure/Reservation.api";
import {
  quotationsCrudMessages,
  reservationCrudMessages,
  reservationMessages,
} from "./reservation.messages";
import {
  APP_COLORS,
  BLACK_LIST_TC_PARTNER,
  EMAIL_STATUS,
  FEATURE_TAGS,
  NO_SHOW_STATUS,
  ORGANIZATION_TYPE,
  PAYMENT_STATUS,
  PAYMENT_TYPES_FILTER_VALUES,
  PAYMENT_TYPES_VALUES,
  RERSERVATION_STATUS,
  SHOW_CC_CARD_PAYMENT_METHODS,
  WHITE_LIST_TC_PARTNER,
} from "../witadmin/Common/constants";

const ALLOWED_QUOTATION_COLUMNS_AND_FILTERS = [
  "propertyId",
  "bookingId",
  "multipleBookingId",
  "bookingDate",
  "checkin",
  "checkout",
  "name",
  "email",
  "inventoryTicker",
  "status",
  "totalAmount",
  "guaranteeAmount",
  "currency",
  "paymentType",
  "affiliate",
  "roomNights",
  "extras",
  "insurance",
  "promotions",
  "promoCode",
  "cancellationDate",
  "tierTicker",
  "organizationName",
  "organizationType",
  "origin",
  "mode",
  "locale",
  "tags",
];

const ALL_PROPERTIES_FILTER = "all";

const ColorSquare = styled.span<{ color: string }>`
  font-size: 22px;
  margin-top: -3px;
  margin-right: 5px !important;
  color: ${props => APP_COLORS[props.color]};
`;

const LegendRow = styled.div`
  > * {
    margin-right: 15px;
  }

  *:first-child {
    font-weight: 600;
  }

  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const StyledLink = styled.button`
  color: inherit;

  &:hover {
    color: inherit;
    text-decoration: underline;
  }
`;

const defaultPageSize = 50;
const pageSizes = [50, 100, 500];

const tooltipConfig = {
  delay: { show: 250, hide: 0 },
};

interface ButtonConfig extends ButtonProps {
  className: string;
}

const buttonConfig: ButtonConfig = {
  disabled: false,
  size: "sm",
  className: "mr-2",
  variant: "light",
};

// @ts-ignore
const editTooltip = props => (
  <Tooltip id="edit-tooltip" {...props}>
    <FormattedMessage {...reservationMessages.edit} />
  </Tooltip>
);
const historicalTooltip = (props: any) => (
  <Tooltip id="historical-tooltip" {...props}>
    <FormattedMessage {...reservationMessages.historical} />
  </Tooltip>
);
const automaticChargeTooltip = (props: any) => (
  <Tooltip id="automatic-charge-tooltip" {...props}>
    <FormattedMessage {...reservationMessages.automaticCharges} />
  </Tooltip>
);
const SendUnmaskedTooltip = (props: any) => (
  <Tooltip id="send-unmasked-tooltip" {...props}>
    <FormattedMessage {...reservationMessages.sendUnmaskedReservationTooltip} />
  </Tooltip>
);
const SendQuotationTooltip = (props: any) => (
  <Tooltip id="send-quotation-tooltip" {...props}>
    <FormattedMessage {...reservationMessages.sendQuotationTooltip} />
  </Tooltip>
);
const ConfirmQuotationTooltip = (props: any) => (
  <Tooltip id="send-quotation-tooltip" {...props}>
    <FormattedMessage {...reservationMessages.confirmQuotation} />
  </Tooltip>
);
// @ts-ignore
const detailTooltip = props => (
  <Tooltip id="detail-tooltip" {...props}>
    <FormattedMessage {...reservationMessages.detail} />
  </Tooltip>
);
// @ts-ignore
const cancelTooltip = props => (
  <Tooltip id="cancel-tooltip" {...props}>
    <FormattedMessage {...reservationMessages.cancel} />
  </Tooltip>
);
// @ts-ignore
const confirmTooltip = props => (
  <Tooltip id="confirm-tooltip" {...props}>
    <FormattedMessage {...reservationMessages.confirm} />
  </Tooltip>
);
// @ts-ignore
const reactivateTooltip = props => (
  <Tooltip id="reactivate-tooltip" {...props}>
    <FormattedMessage {...reservationMessages.reactivate} />
  </Tooltip>
);
// @ts-ignore
const showCCTooltip = props => (
  <Tooltip id="showCC-tooltip" {...props}>
    <FormattedMessage {...reservationMessages.showCC} />
  </Tooltip>
);
// @ts-ignore
const emailResendTooltip = props => (
  <Tooltip id="emailResend-tooltip" {...props}>
    <FormattedMessage {...reservationMessages.emailResend} />
  </Tooltip>
);
// @ts-ignore
const noShowTooltip = props => (
  <Tooltip id="showNoShow-tooltip" {...props}>
    <FormattedMessage {...reservationMessages.noShow} />
  </Tooltip>
);
// @ts-ignore
const showTooltip = props => (
  <Tooltip id="noShow-tooltip" {...props}>
    <FormattedMessage {...reservationMessages.show} />
  </Tooltip>
);

function isNoShowAvailable(
  paymentStatus: string,
  status: string,
  checkout: string,
  isMultiProperty: boolean
) {
  return (
    paymentStatus !== PAYMENT_STATUS.FAILED &&
    paymentStatus !== PAYMENT_STATUS.EXPIRED &&
    status !== RERSERVATION_STATUS.PEND &&
    status !== RERSERVATION_STATUS.CXL &&
    !isMultiProperty &&
    calculateCanEdit(checkout)
  );
}

const CustomStyledTableRow = styled(StyledTableRow)`
  ${({ row }: { row: ReservationProps }) => {
    const reservation = Reservation.of(row);
    if (reservation.isSuccess) {
      return `color:${APP_COLORS["success"]}`;
    }
    if (reservation.isFailed) {
      return `color:${APP_COLORS["danger"]}`;
    }
    if (reservation.isNotCompleted) {
      return `color:${APP_COLORS["warning"]}`;
    }
    if (reservation.isTransferPending || reservation.isQuotationPending) {
      return `color:${APP_COLORS["info"]}`;
    }
    if (reservation.isExpired) {
      return `color:${APP_COLORS["purple"]}`;
    }
    if (reservation.isCancelled) {
      return `color:${APP_COLORS["gray-darkish"]}`;
    }
  }}
`;

function AdvanceRenderer({ value = "" }: RendererProps<string | undefined>) {
  return <>{value && Math.round(Number(value) / 24)}</>;
}

const StoreOrderLinkRenderer = ({ value }: RendererProps<string>) => {
  if (value && OrderItemCodeVo.isValid(value)) {
    return (
      <a
        className="text-inherit hover:text-inherit"
        href={`store/orders/${value}`}
        target="_blank"
        rel="noopener noreferrer">
        {value}
      </a>
    );
  }

  return <span className="text-inherit">{value}</span>;
};

const columns = [
  "bookingId",
  "multipleBookingId",
  "extendedReservationId",
  "bookingDate",
  "checkin",
  "checkout",
  "name",
  "email",
  "phone",
  "rooms",
  "inventoryTicker",
  "bundleTicker",
  "capacity",
  "nights",
  "status",
  "totalAmount",
  "guaranteeAmount",
  "paymentType",
  "userEmailStatus",
  "hotelEmailStatus",
  "affiliate",
  "roomNights",
  "lastModificationDate",
  "accommodationTicker",
  "configurationTicker",
  "mealPlanTicker",
  "conditionTicker",
  "advance",
  "extras",
  "insurance",
  "promotions",
  "comments",
  "internalNotes",
  "isCombined",
  "promoCode",
  "country",
  "onlineStoreOrderId",
  "cancellationDate",
  "tierTicker",
  "organizationName",
  "organizationType",
  "origin",
  "mode",
  "locale",
  "communicationConsent",
  "tags",
  "modifierUsers",
  "lastEditor",
];

// Toggling disabled columns (compulsory)
const columnVisibilityExtensions = [
  "bookingId",
  "multipleBookingId",
  "checkin",
  "checkout",
  "rooms",
  "name",
  "roomNights",
  "status",
  "__actions",
].map(it => ({ columnName: it, togglingEnabled: false }));

// Initially hidden columns
const initialHiddenColumnNames = [
  "propertyId",
  "extendedReservationId",
  "bookingDate",
  "email",
  "phone",
  "inventoryTicker",
  "bundleTicker",
  "capacity",
  "nights",
  "totalAmount",
  "guaranteeAmount",
  "currency",
  "paymentType",
  "userEmailStatus",
  "hotelEmailStatus",
  "affiliate",
  "lastModificationDate",
  "accommodationTicker",
  "configurationTicker",
  "mealPlanTicker",
  "conditionTicker",
  "advance",
  "extras",
  "insurance",
  "promotions",
  "comments",
  "internalNotes",
  "country",
  "onlineStoreOrderId",
  "cancellationDate",
  "cancellationReason",
  "tierTicker",
  "organizationName",
  "organizationType",
  "isCombined",
  "promoCode",
  "origin",
  "mode",
  "noShow",
  "locale",
  "communicationConsent",
  "tags",
  "modifierUsers",
];

function calculateCanConfirm(
  status: keyof typeof RERSERVATION_STATUS,
  paymentStatus: keyof typeof PAYMENT_STATUS,
  isWitAdmin: boolean
): boolean {
  if (isWitAdmin && status === RERSERVATION_STATUS.CXL) {
    return true;
  }

  return (
    status === RERSERVATION_STATUS.KO ||
    status === RERSERVATION_STATUS.PEND ||
    paymentStatus === PAYMENT_STATUS.PENDING
  );
}

function calculateElapsedMinutesSinceCreation(bookingDate: string) {
  // our db stores the dates in UTC
  const bookingDateUTC = parseISO(bookingDate + "Z");
  const currentDateUTC = new Date();
  return differenceInMinutes(currentDateUTC, bookingDateUTC);
}

// Behavior to calculate if we are inside the billing period
const billingDate = 4;
const currentMonth = new Date().getMonth();
let previousMonth = new Date(new Date().setMonth(currentMonth - 1));
previousMonth = new Date(previousMonth.setDate(1));
previousMonth = new Date(previousMonth.setHours(0, 0, 0));

const afterBillingDate = isAfter(
  new Date(new Date().toLocaleString("en-US", { timeZone: "Europe/Madrid" })),
  new Date().setDate(billingDate)
);

function calculateCanEdit(checkout: string) {
  const startOfMonth = new Date();
  startOfMonth.setDate(1);
  startOfMonth.setHours(0, 0, 0, 0);

  // If past the billing day of current month, only allow cancellation for the current month onward
  if (!afterBillingDate && checkout && isBefore(new Date(checkout), previousMonth)) {
    return false;
  }

  // If not past the billing day of current month, allow cancellation for the previous month as well
  if (afterBillingDate && checkout && isBefore(new Date(checkout), startOfMonth)) {
    return false;
  }
  return true;
}

// TODO: remove this code after resilience project is working
function extractShowInvalidPendingReservations() {
  try {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    return urlParams.get("showInvalidPending") === "true";
  } catch (e) {
    return false;
  }
}

function filterColumns<T>(
  columnsObj: Partial<TableColumns<T>>,
  allowedColumns: string[] | null
): TableColumns<T> {
  if (allowedColumns) {
    return allowedColumns.reduce((acc, key) => {
      if (columnsObj[key as keyof TableColumns<T>]) {
        acc[key as keyof TableColumns<T>] = columnsObj[key as keyof TableColumns<T>];
      }
      return acc;
    }, {} as TableColumns<T>);
  }
  return columnsObj as TableColumns<T>;
}

function filterFilters(
  filtersObj: Record<string, any>,
  allowedFilters: string[] | null
): Record<string, any> {
  if (allowedFilters) {
    return allowedFilters.reduce((acc, key) => {
      if (filtersObj[key]) {
        acc[key] = filtersObj[key];
      }
      return acc;
    }, {} as Record<string, any>);
  }
  return filtersObj;
}

function calculateFilterPropertyTickers(
  multiProperty: boolean,
  userPropertyTickers: string[],
  propertyTicker: string,
  filterValues: { propertyId?: string },
  showPendingQuotations?: boolean
): string[] {
  if (showPendingQuotations && !filterValues.propertyId) {
    return [propertyTicker];
  }
  if (multiProperty && filterValues?.propertyId === ALL_PROPERTIES_FILTER) {
    return userPropertyTickers;
  }
  return multiProperty
    ? filterValues.propertyId
      ? [filterValues.propertyId]
      : userPropertyTickers
    : [propertyTicker];
}

export default function ReservationCrud({
  propertyTicker,
  url,
  multiProperty,
  email,
  showPendingQuotations,
}: {
  propertyTicker: string;
  url: string;
  multiProperty: boolean;
  email: string | undefined;
  showPendingQuotations?: boolean;
}) {
  const property = useProperty();
  const authorizer = useAuthorizer();
  const userPropertyTickers = useUserPropertyTickers();
  const propertyConfiguration = usePropertyConfiguration();
  const userAuthInfo = useUserAuthInfo();
  const config = useConfig();
  const allowedLocales = usePropertyLocales();
  const httpClient = useApiClient();
  const keycloakAuthz = useKeycloakAuthz();
  const intl = useIntl();
  const history = useHistory();
  const userProfile = useUserProfile();
  const isCallCenterUser = useRoleChecker([CALL_CENTER_LIGHT_USER, CALL_CENTER_USER]);
  const showOnlyCallCenterReservations =
    useRoleChecker([CALL_CENTER_LIGHT_USER]) && propertyTicker?.includes("slk_");

  const [exportMaskedLoading, setExportMaskedLoading] = useState(false);
  const [currentFilterValues, setCurrentFilterValues] = useState<FilterValues>({});
  const [loadRetryAttempt, setLoadRetryAttempt] = useState(0);
  const [loadingShowCC, setLoadingShowCC] = useState<{
    propertyTicker: string;
    reservationId: string;
  }>();

  const reservationApi = useMemo(() => new ReservationApi(httpClient), [httpClient]);

  const paymentsApi = useMemo(() => {
    return new PaymentsApi(config.paymentsApiEndpoint, propertyTicker);
  }, [config, propertyTicker]);

  const lastSlashIndex = useMemo(() => url.lastIndexOf("/"), [url]);
  const urlNoTicker = useMemo(() => url.substring(0, lastSlashIndex), [url, lastSlashIndex]);
  const showHistoricalReservations = useMemo(
    () => propertyConfiguration?.showHistoricalReservations === "1" && !showPendingQuotations,
    [propertyConfiguration, showPendingQuotations]
  );

  const handleGoToAutomaticCharges = useCallback(
    () => history.push(`/${property.ticker}/automaticCharges`),
    [history, property.ticker]
  );

  const onExportMasked = useCallback(async () => {
    const propertyTickers = multiProperty
      ? typeof currentFilterValues.propertyId === "string"
        ? [currentFilterValues.propertyId]
        : userPropertyTickers
      : [propertyTicker];

    const filters = {
      ...currentFilterValues,
      showInvalidPending: extractShowInvalidPendingReservations(),
      showOnlyCallCenterReservations,
    };

    setExportMaskedLoading(true);
    const csvData = await reservationApi
      .exportMasked(propertyTickers, filters)
      .finally(() => setExportMaskedLoading(false));
    createFileDownloadFromRawData(
      csvData,
      `reservations_export_${format(new Date(), "yyyyMMdd")}.csv`
    );
  }, [
    multiProperty,
    userPropertyTickers,
    propertyTicker,
    showOnlyCallCenterReservations,
    reservationApi,
    currentFilterValues,
  ]);

  const onExportUnmasked = useCallback(
    async ({ filterValues }) => {
      const propertyTickers: string[] = filterValues.propertyId
        ? [filterValues.propertyId]
        : userPropertyTickers;

      const filters = {
        ...filterValues,
        showInvalidPending: extractShowInvalidPendingReservations(),
        showOnlyCallCenterReservations,
      };

      try {
        await (multiProperty
          ? reservationApi.exportUnmaskedMultiProperty(propertyTickers, filters)
          : reservationApi.exportUnmasked(propertyTicker, filters));
        await SweetAlert.fire({
          icon: "info",
          text: intl.formatMessage(reservationMessages?.sendUnmaskedListEmailSuccess, {
            email: userProfile?.email,
          }),
          cancelButtonText: `${intl.formatMessage(reservationMessages?.alertSuccesTextBack)}`,
        });
      } catch (e) {
        if (e instanceof AxiosError && e.response && e.response.status === 429) {
          await SweetAlert.fire({
            icon: "warning",
            text: intl.formatMessage(reservationMessages?.unmaskedErrorTooManyRequests),
            cancelButtonText: `${intl.formatMessage(reservationMessages?.alertSuccesTextBack)}`,
          });
        } else {
          await SweetAlert.fire({
            icon: "error",
            text: intl.formatMessage(reservationMessages?.sendUnmaskedListEmailError),
            cancelButtonText: `${intl.formatMessage(reservationMessages?.alertSuccesTextBack)}`,
          });
        }
      }
    },
    [
      userPropertyTickers,
      showOnlyCallCenterReservations,
      multiProperty,
      reservationApi,
      propertyTicker,
      intl,
      userProfile?.email,
    ]
  );

  const handleDetail = useCallback(
    ({ propertyId, bookingId }: any) => {
      history.push(`${urlNoTicker}/${propertyId}/reservation-detail/${bookingId}`);
    },
    [history, urlNoTicker]
  );

  const onGetAll = useCallback(
    async ({ filterValues, currentPage, pageSize, setTotalRowsCount }): Promise<Reservation[]> => {
      setCurrentFilterValues(filterValues);
      if (multiProperty && (!userPropertyTickers || !userPropertyTickers.length)) {
        if (loadRetryAttempt <= 2) {
          setLoadRetryAttempt(loadRetryAttempt + 1);
        }
        // mini hack to show loader while userPropertyTickers are not loaded
        return new Promise(() => {});
      }

      const propertyTickers: string[] = calculateFilterPropertyTickers(
        multiProperty,
        userPropertyTickers,
        propertyTicker,
        filterValues,
        showPendingQuotations
      );

      const filters = {
        ...filterValues,
        paymentType: showPendingQuotations ? "deferred" : filterValues?.paymentType,
        showInvalidPending: extractShowInvalidPendingReservations(),
        showOnlyCallCenterReservations,
        status: showPendingQuotations ? RERSERVATION_STATUS.PEND : filterValues?.status,
        statusConfig: showPendingQuotations ? 4 : filterValues?.statusConfig,
        tags: showPendingQuotations ? FEATURE_TAGS.quotation : filterValues?.tags,
      };

      const page = multiProperty
        ? await reservationApi.getAllMultiProperty(
            propertyTickers,
            filters,
            {
              currentPage,
              pageSize,
            },
            showPendingQuotations
          )
        : await reservationApi.getAll(
            propertyTickers,
            filters,
            { currentPage, pageSize },
            showPendingQuotations
          );
      setTotalRowsCount(page.totalElements);

      return page.content;
    },
    [
      multiProperty,
      userPropertyTickers,
      propertyTicker,
      showOnlyCallCenterReservations,
      reservationApi,
      loadRetryAttempt,
      showPendingQuotations,
    ]
  );

  const handleSendEmail = useCallback(
    async (type: string, propertyId: string, bookingId: string) => {
      await SweetAlert.fire({
        icon: "question",
        showCancelButton: true,
        text: intl.formatMessage(reservationMessages.emailConfirmation),
        confirmButtonColor: APP_COLORS.primary,
        cancelButtonText: intl.formatMessage(reservationMessages.cancel),
      }).then(result => {
        if (result.isConfirmed) {
          reservationApi.sendReservationByEmail(bookingId, propertyId, type);
        }
      });
    },
    [intl, reservationApi]
  );

  const handleCancelReservation = useCallback(
    async (
      propertyId: string,
      bookingId: string,
      reload: () => void,
      name: string,
      multipleBookingId: string
    ) => {
      // TODO: revert this when security has stabilized
      const cancellationReason = "";
      // const cancellationReason = `<div class="mt-3">
      //     <input
      //       type="text"
      //       class="form-control"
      //       id="cancellation-reason"
      //       placeholder="${intl.formatMessage(
      //         reservationCrudMessages.tableHeaders.cancellationReason
      //       )}"
      //     />
      //   </div>`
      await SweetAlert.fire({
        showCancelButton: true,
        html: `<div><div style="color:${APP_COLORS.primary};font-weight: bold">${intl.formatMessage(
          reservationMessages.cancellationConfirmation
        )}</div> <br>
        <div style="color:${APP_COLORS["gray-darkish"]}"><b >${intl.formatMessage(
          reservationCrudMessages.tableHeaders.name
        )}</b>: ${name}<br></div>
        <div style="margin-top:10px;color:${APP_COLORS["gray-darkish"]}"><b >${intl.formatMessage(
          reservationCrudMessages.tableHeaders.bookingId
        )}</b>: ${bookingId}<br></div>
        <div style="margin-top:10px;color:${APP_COLORS["gray-darkish"]}"><b >${intl.formatMessage(
          reservationCrudMessages.tableHeaders.multipleBookingId
        )}</b>: ${multipleBookingId}</div> 
        ${cancellationReason}
        </div>`,
        confirmButtonColor: APP_COLORS.primary,
        confirmButtonText: intl.formatMessage(reservationMessages.yes),
        cancelButtonText: intl.formatMessage(reservationMessages.no),
        preConfirm: async () => {
          SweetAlert.showLoading();

          const token =
            propertyId !== propertyTicker
              ? await getPropertyRpt(propertyId, keycloakAuthz)
              : undefined;
          await reservationApi
            .cancelReservation(
              bookingId,
              propertyId,
              // @ts-ignore
              document.getElementById("cancellation-reason")?.value,
              token
            )
            .catch(() => {
              SweetAlert.showValidationMessage(`Request failed`);
            });
          SweetAlert.hideLoading();
        },
      }).then(async result => {
        if (result.isConfirmed) {
          reload();
        }
      });
    },
    [intl, keycloakAuthz, propertyTicker, reservationApi]
  );

  const handleConfirmReservation = useCallback(
    async (propertyId: string, bookingId: string, paymentType: string, reload: () => void) => {
      await SweetAlert.fire({
        icon: "question",
        showCancelButton: true,
        text: intl.formatMessage(reservationMessages.confirmationConfirmation),
        confirmButtonColor: APP_COLORS.primary,
        cancelButtonText: intl.formatMessage(reservationMessages.cancel),
        preConfirm: async () => {
          SweetAlert.showLoading();
          await reservationApi.confirmReservation(bookingId, propertyId, paymentType).catch(() => {
            SweetAlert.showValidationMessage(`Request failed`);
          });
          SweetAlert.hideLoading();
        },
      }).then(async result => {
        if (result.isConfirmed) {
          reload();
        }
      });
    },
    [intl, reservationApi]
  );

  const handleCheckCC = useCallback(
    async (multipleBookingId: string, reservationPropertyTicker: string, paymentType: string) => {
      if (!userProfile?.email || userProfile?.email === "") {
        return alert("Invalid user email");
      }

      if (paymentType === PAYMENT_TYPES_VALUES.pay_no_pain_proxy) {
        SweetAlert.showLoading();
        try {
          const rptToken =
            reservationPropertyTicker !== propertyTicker
              ? await getPropertyRpt(reservationPropertyTicker, keycloakAuthz)
              : authorizer.getRpt() || "";
          await httpClient.post(
            `/api/property/${reservationPropertyTicker}/engine/payments/paynopainproxy/cards/reservation/${multipleBookingId}/link`,
            {
              backofficeUserEmail: userProfile?.email,
            },
            {
              headers: {
                Authorization: `Bearer ${rptToken}`,
                "x-property": reservationPropertyTicker,
              },
            }
          );
          SweetAlert.close();
          const result = await SweetAlert.fire({
            title: intl.formatMessage(reservationMessages.alertSucces),
            icon: "info",
            html: `
                <p> ${intl.formatMessage(reservationMessages.willReceiveEmail, {
                  email: userProfile.email,
                })} </p>
              `,
            cancelButtonText: `${intl.formatMessage(reservationMessages.alertSuccesTextBack)}`,
          });

          if (!result.isConfirmed || result.isDismissed) {
            return Promise.resolve(false);
          }
        } catch (error) {
          SweetAlert.close();
          await SweetAlert.fire({
            title: intl.formatMessage(reservationMessages.alertWarning),
            icon: "warning",
            html: `<p>${intl.formatMessage(reservationMessages.alertWarningText)}</p>`,
          });
        }
      } else if (PAYMENT_TYPES_VALUES.septeo_vault === paymentType) {
        try {
          setLoadingShowCC({ propertyTicker, reservationId: multipleBookingId });
          const url = await paymentsApi.generateSepteoCardShowLink(
            reservationPropertyTicker,
            multipleBookingId,
            keycloakAuthz.rpt
          );
          window.open(url, "_blank");
        } catch (e) {
          console.error("Error generating Septeo card show link", e);
        } finally {
          setLoadingShowCC(undefined);
        }
      } else {
        window.open(
          `https://secure.witbooking.com/login?reservationID=${multipleBookingId}&ticker=${reservationPropertyTicker}`,
          "_blank"
        );
      }
    },
    [authorizer, httpClient, intl, keycloakAuthz, paymentsApi, propertyTicker, userProfile?.email]
  );

  const handleNoShowConfirmation = useCallback(
    async (propertyTicker: string, noShowStatus: string, bookingId: string, reload: () => void) => {
      await SweetAlert.fire({
        icon: "question",
        html: `<div><div style="color:${APP_COLORS["gray-dark"]};font-weight: bold">${
          noShowStatus === NO_SHOW_STATUS.NO_SHOW
            ? intl.formatMessage(reservationMessages.markAsShowed)
            : intl.formatMessage(reservationMessages.markAsNoShow)
        }</div><br>
        <div>${intl.formatMessage(reservationMessages.doYouWantToContinue)} </div>
        </div>`,
        showCancelButton: true,
        confirmButtonColor: APP_COLORS.primary,
        cancelButtonText: intl.formatMessage(reservationMessages.cancel),
        preConfirm: async () => {
          SweetAlert.showLoading();
          await reservationApi
            .noShowReservation(bookingId, propertyTicker, noShowStatus)
            .catch(() => {
              SweetAlert.showValidationMessage(`Request failed`);
            });
          SweetAlert.hideLoading();
        },
      }).then(async result => {
        if (result.isConfirmed) {
          reload();
        }
      });
    },
    [intl, reservationApi]
  );

  const IdRenderer = useCallback(
    ({ value, rows }: RendererProps<string>) => {
      return <StyledLink onClick={() => handleDetail(rows)}>{value}</StyledLink>;
    },
    [handleDetail]
  );

  const goToAutomaticCharges = useCallback(
    async (propertyId?: string, roomStayId?: string) => {
      if (!propertyId) {
        return;
      }

      const localFilters = JSON.parse(window.localStorage.getItem("asyncFilterValues") || "") || {};
      const filters = {
        ...localFilters,
        automaticCharge: {
          ...localFilters.automaticCharge,
          bookingId: roomStayId,
        },
      };
      window.localStorage.setItem("asyncFilterValues", JSON.stringify(filters));
      history.push(`${urlNoTicker}/${propertyId}/automaticCharges`);
    },
    [history, urlNoTicker]
  );

  const formattedColumns: TableColumns<any> = useMemo(() => {
    const columns: Partial<TableColumns<any>> = {
      propertyId: {
        renderer: StringRenderer,
        width: 125,
      },
      bookingId: {
        renderer: IdRenderer,
        width: 125,
      },
      multipleBookingId: {
        renderer: IdRenderer,
        width: 100,
      },
      extendedReservationId: {
        renderer: StringRenderer,
        width: 150,
      },
      bookingDate: {
        renderer: mapRenderer(CustomDateTimeRenderer, v => v, {
          timeZone: propertyConfiguration?.timeZone,
        }),
        width: 150,
      },
      checkin: {
        renderer: StringRenderer,
        width: 100,
      },
      checkout: {
        renderer: StringRenderer,
        width: 100,
      },
      name: {
        renderer: ({ rows }) =>
          StringRenderer({ value: rows ? `${rows.customer.lastName} ${rows.customer.name}` : "" }),
        width: 175,
      },
      email: {
        renderer: ({ rows }) => StringRenderer({ value: rows?.customer?.email ?? "" }),
        width: 200,
      },
      phone: {
        renderer: ({ rows }) => StringRenderer({ value: rows?.customer?.phone ?? "" }),
        width: 150,
      },
      rooms: {
        renderer: StringRenderer,
        width: 50,
      },
      inventoryTicker: {
        renderer: StringRenderer,
        width: 150,
      },
      bundleTicker: {
        renderer: StringRenderer,
        width: 200,
      },
      capacity: {
        renderer: StringRenderer,
        width: 50,
      },
      nights: {
        renderer: StringRenderer,
        width: 75,
      },
      status: {
        renderer: StringRenderer,
        width: 100,
      },
      noShowLabel: {
        renderer: ({ rows }) => {
          let noShowLabel = "";
          if (rows && rows.noShow.toUpperCase() === NO_SHOW_STATUS.NO_SHOW) {
            noShowLabel = NO_SHOW_STATUS.NO_SHOW;
          } else if (rows && rows.noShow.toUpperCase() === NO_SHOW_STATUS.SHOWED) {
            noShowLabel = NO_SHOW_STATUS.SHOWED;
          }

          return StringRenderer({ value: noShowLabel });
        },
        width: 100,
      },
      totalAmount: {
        renderer: ({ rows }) => StringRenderer({ value: rows?.totalAmount?.toFixed(2) }),
        width: 100,
      },
      guaranteeAmount: {
        renderer: ({ rows }) => StringRenderer({ value: rows?.deposit?.toFixed(2) }),
        width: 100,
      },
      currency: {
        renderer: StringRenderer,
        width: 100,
      },
      paymentType: {
        renderer: ({ value, config, button, rows }: RendererProps<string>) =>
          StringRenderer({
            value:
              value && reservationMessages[value]
                ? intl.formatMessage(reservationMessages[value])
                : value,
            config,
            button,
            rows,
          }),
        width: 150,
      },
      userEmailStatus: {
        renderer: StringRenderer,
        width: 125,
        button: ({ propertyId, bookingId }: { propertyId: string; bookingId: string }) => (
          <OverlayTrigger {...tooltipConfig} overlay={emailResendTooltip}>
            <Button
              {...buttonConfig}
              onClick={() => handleSendEmail("GUEST", propertyId, bookingId)}>
              <FontAwesomeIcon icon={faEnvelope} />
            </Button>
          </OverlayTrigger>
        ),
      },
      hotelEmailStatus: {
        renderer: StringRenderer,
        width: 125,
        button: ({ propertyId, bookingId }: { propertyId: string; bookingId: string }) => (
          <OverlayTrigger {...tooltipConfig} overlay={emailResendTooltip}>
            <Button
              {...buttonConfig}
              onClick={() => handleSendEmail("PROPERTY", propertyId, bookingId)}>
              <FontAwesomeIcon icon={faEnvelope} />
            </Button>
          </OverlayTrigger>
        ),
      },
      affiliate: {
        renderer: StringRenderer,
        width: 200,
      },
      roomNights: {
        renderer: ({ rows }) =>
          StringRenderer({ value: rows ? String(rows.nights * rows.rooms) : "" }),
        width: 50,
      },
      lastModificationDate: {
        renderer: mapRenderer(CustomDateTimeRenderer, v => v, {
          timeZone: propertyConfiguration?.timeZone,
        }),
        width: 150,
      },
      accommodationTicker: {
        renderer: StringRenderer,
        width: 100,
      },
      configurationTicker: {
        renderer: StringRenderer,
        width: 100,
      },
      mealPlanTicker: {
        renderer: StringRenderer,
        width: 75,
      },
      conditionTicker: {
        renderer: StringRenderer,
        width: 75,
      },
      advance: {
        renderer: AdvanceRenderer,
        width: 100,
      },
      extras: {
        renderer: StringRenderer,
        width: 100,
      },
      insurance: {
        renderer: StringRenderer,
        width: 100,
      },
      promotions: {
        renderer: StringRenderer,
        width: 125,
      },
      promoCode: {
        renderer: StringRenderer,
        width: 125,
      },
      comments: {
        renderer: BooleanRenderer,
        width: 125,
        align: "center",
      },
      internalNotes: {
        renderer: BooleanRenderer,
        width: 125,
        align: "center",
      },
      isCombined: {
        renderer: BooleanRenderer,
        width: 150,
        align: "center",
      },
      country: {
        renderer: StringRenderer,
        width: 50,
      },
      onlineStoreOrderId: {
        renderer: StoreOrderLinkRenderer,
        width: 130,
      },
      cancellationDate: {
        renderer: mapRenderer(CustomDateTimeRenderer, v => v, {
          timeZone: propertyConfiguration?.timeZone,
        }),
        width: 125,
      },
      cancellationReason: {
        renderer: StringRenderer,
        width: 200,
      },
      tierTicker: {
        renderer: StringRenderer,
        width: 125,
      },
      organizationName: {
        renderer: StringRenderer,
        width: 125,
      },
      organizationType: {
        renderer: StringRenderer,
        width: 125,
      },
      origin: {
        renderer: StringRenderer,
        width: 125,
      },
      mode: {
        renderer: StringRenderer,
        width: 125,
      },
      locale: {
        renderer: StringRenderer,
        width: 125,
      },
      communicationConsent: {
        renderer: BooleanRenderer,
        width: 150,
        align: "center",
      },
      tags: {
        renderer: StringRenderer,
        width: 200,
      },
      modifierUsers: {
        renderer: StringRenderer,
        width: 200,
      },
      lastEditor: {
        renderer: StringRenderer,
        width: 200,
      },
    };

    const allowedColumns = showPendingQuotations ? ALLOWED_QUOTATION_COLUMNS_AND_FILTERS : null;
    return filterColumns<any>(columns, allowedColumns);
  }, [propertyConfiguration?.timeZone, showPendingQuotations, IdRenderer, handleSendEmail, intl]);

  if (multiProperty) {
    columns.unshift("propertyId");
  }
  if (!multiProperty) {
    delete formattedColumns.propertyId;
  }

  const tableFilters: TableFilters<any, any> = useMemo(() => {
    const tFilters: TableFilters<any, any> = columns.reduce(
      (accum, curr) => ({
        ...accum,
        [curr]: {
          filterFn: "async",
          widget: inputFilter,
        },
      }),
      {}
    );
    delete tFilters.roomNights;
    delete tFilters.comments;
    delete tFilters.internalNotes;
    delete tFilters.phone;
    delete tFilters.paymentType;
    delete tFilters.noShow;
    if (showOnlyCallCenterReservations) {
      delete tFilters.origin;
    }

    tFilters.checkin = {
      filterFn: "async",
      widget: dateFilter("checkin", intl),
    };
    tFilters.checkout = {
      filterFn: "async",
      widget: dateFilter("checkout", intl),
    };
    tFilters.bookingDate = {
      filterFn: "async",
      widget: dateFilter("bookingDate", intl),
    };
    tFilters.lastModificationDate = {
      filterFn: "async",
      widget: dateFilter("lastModificationDate", intl),
    };
    tFilters.cancellationDate = {
      filterFn: "async",
      widget: dateFilter("cancellationDate", intl),
    };
    tFilters.hasComments = {
      filterFn: "async",
      widget: selectFilter(intl, reservationMessages.hasComments),
    };
    tFilters.hasInternalNotes = {
      filterFn: "async",
      widget: selectFilter(intl, reservationMessages.hasInternalNotes),
    };
    tFilters.noShow = {
      filterFn: "async",
      widget: selectFilter(intl, reservationMessages.noShowFilter),
    };
    tFilters.status = {
      filterFn: "async",
      widget: selectorFilter(
        <FormattedMessage {...reservationCrudMessages.tableHeaders?.status} />,
        Object.values(RERSERVATION_STATUS).map(value => ({ value: value, label: value }))
      ),
    };
    tFilters.currency = {
      filterFn: "async",
      widget: inputFilter,
    };
    tFilters.paymentType = {
      filterFn: "async",
      widget: selectorFilter(
        <FormattedMessage {...reservationCrudMessages.tableHeaders?.paymentType} />,
        Object.entries(PAYMENT_TYPES_FILTER_VALUES).map(([key, value]) => {
          return {
            value: value,
            label: intl.formatMessage(reservationMessages[key]),
          };
        })
      ),
    };
    tFilters.userEmailStatus = {
      filterFn: "async",
      widget: selectorFilter(
        <FormattedMessage {...reservationCrudMessages.tableHeaders?.userEmailStatus} />,
        Object.values(EMAIL_STATUS).map(value => ({ value: value, label: value }))
      ),
    };
    tFilters.hotelEmailStatus = {
      filterFn: "async",
      widget: selectorFilter(
        <FormattedMessage {...reservationCrudMessages.tableHeaders?.hotelEmailStatus} />,
        Object.values(EMAIL_STATUS).map(value => ({ value: value, label: value }))
      ),
    };
    tFilters.organizationType = {
      filterFn: "async",
      widget: selectorFilter(
        <FormattedMessage {...reservationCrudMessages.tableHeaders?.organizationType} />,
        Object.values(ORGANIZATION_TYPE).map(value => ({
          value: value,
          label: intl.formatMessage(reservationMessages[value]),
        }))
      ),
    };
    if (multiProperty) {
      tFilters.propertyId = {
        filterFn: "async",
        widget: selectorFilter(
          <FormattedMessage {...reservationCrudMessages.tableHeaders?.propertyId} />,
          [
            { value: ALL_PROPERTIES_FILTER, label: intl.formatMessage(reservationMessages.all) },
            ...userPropertyTickers.map(ticker => ({ value: ticker, label: ticker })),
          ]
        ),
      };
    }
    tFilters.locale = {
      filterFn: "async",
      widget: selectorFilter(
        <FormattedMessage {...reservationCrudMessages.tableHeaders?.locale} />,
        allowedLocales.map(value => ({
          value,
          label: value,
        }))
      ),
    };
    tFilters.communicationConsent = {
      filterFn: "async",
      widget: selectFilter(intl, reservationMessages.hasCommunicationConsent),
    };
    tFilters.tags = {
      filterFn: "async",
      widget: selectorFilter(
        <FormattedMessage {...reservationCrudMessages.tableHeaders.tags} />,
        Object.entries(FEATURE_TAGS).map(([key, value]) => ({
          value: value,
          // `selectorFilter` types `label` as string, so formatMessage (not <FormattedMessage/>).
          label: intl.formatMessage(FEATURE_TAG_MESSAGES[key as keyof typeof FEATURE_TAGS]),
        }))
      ),
    };
    const allowedFilters = showPendingQuotations ? ALLOWED_QUOTATION_COLUMNS_AND_FILTERS : null;
    return filterFilters(tFilters, allowedFilters);
  }, [
    showOnlyCallCenterReservations,
    intl,
    multiProperty,
    allowedLocales,
    userPropertyTickers,
    showPendingQuotations,
  ]);

  function TableRowActions(props: TableRowActionProps<any>) {
    const apiClient = useApiClient();
    const api = useMemo(() => new Api(apiClient, propertyTicker), [apiClient]);
    const [sendEmailLoading, setSendEmailLoading] = useState(false);
    const partner = userProfile?.email?.substring(userProfile?.email.indexOf("@") + 1);
    const hasAutomaticCharges = props?.row?.hasAutomaticCharges;

    const minutesElapsedSinceCreation = calculateElapsedMinutesSinceCreation(
      props?.row?.bookingDate
    );
    const delayEdit = minutesElapsedSinceCreation < 5;

    let showCCButton = false;
    if (partner) {
      showCCButton =
        props?.row?.affiliate !== "BOOKING" &&
        props?.row?.status !== RERSERVATION_STATUS.PEND &&
        (props?.row?.statusConfig === 0 ||
          (SHOW_CC_CARD_PAYMENT_METHODS.includes(props?.row?.paymentType) &&
            props?.row?.statusConfig === 2)) &&
        (PAYMENT_TYPES_VALUES.septeo_vault === props?.row?.paymentType || // septeo vault always allowed
          !BLACK_LIST_TC_PARTNER.includes(partner) ||
          WHITE_LIST_TC_PARTNER[propertyTicker]?.includes(userProfile?.email || ""));
    }

    // TODO: REMOVE THIS!!!!!!!
    const validEmails = [
      "s.elaoufi@witbooking.com",
      "j.lucic@witbooking.com",
      "t.montero@witbooking.com",
      "n.balduzzi@witbooking.com",
    ];
    const email = userProfile?.email || "";
    if (validEmails.includes(email)) {
      showCCButton =
        props?.row?.affiliate !== "BOOKING" &&
        props?.row?.status !== RERSERVATION_STATUS.PEND &&
        (props?.row?.statusConfig === 0 ||
          (SHOW_CC_CARD_PAYMENT_METHODS.includes(props?.row?.paymentType) &&
            props?.row?.statusConfig === 2));
    }

    const canEdit =
      calculateCanEdit(props?.row?.checkout) && props?.row?.status !== RERSERVATION_STATUS.CXL;
    const canConfirm = calculateCanConfirm(
      props?.row?.status,
      props?.row?.paymentStatus,
      Boolean(userAuthInfo?.isWitAdmin)
    );
    const confirmIcon =
      canConfirm &&
      (props?.row?.status === RERSERVATION_STATUS.KO ||
        props?.row?.status === RERSERVATION_STATUS.CXL)
        ? faRedoAlt
        : faCheck;
    const confirmOverlay =
      canConfirm &&
      (props?.row?.status === RERSERVATION_STATUS.KO ||
        props?.row?.status === RERSERVATION_STATUS.CXL)
        ? reactivateTooltip
        : confirmTooltip;
    const isLoadingCC = useMemo(
      () =>
        loadingShowCC &&
        loadingShowCC.propertyTicker === props?.row?.propertyId &&
        loadingShowCC.reservationId === props?.row?.multipleBookingId,
      [props?.row?.multipleBookingId, props?.row?.propertyId]
    );

    const handleSendUnmaskedReservation = useCallback(async () => {
      const reservationId: string | undefined = props?.row?.bookingId;
      const reservationPropertyTicker = props.row.propertyId;
      if (!reservationId || !reservationPropertyTicker) return;

      try {
        setSendEmailLoading(true);
        const token =
          propertyTicker !== reservationPropertyTicker
            ? await getPropertyRpt(reservationPropertyTicker, keycloakAuthz)
            : undefined;
        await reservationApi.sendUnmaskedReservationByEmail(
          reservationPropertyTicker,
          reservationId,
          token
        );
        await SweetAlert.fire({
          icon: "info",
          text: intl.formatMessage(reservationMessages?.sendUnmaskedReservationEmailSuccess, {
            email: userProfile?.email,
          }),
          cancelButtonText: `${intl.formatMessage(reservationMessages?.alertSuccesTextBack)}`,
        });
      } catch (e) {
        if (e instanceof AxiosError && e.response && e.response.status === 429) {
          await SweetAlert.fire({
            icon: "warning",
            text: intl.formatMessage(reservationMessages?.unmaskedErrorTooManyRequests),
            cancelButtonText: `${intl.formatMessage(reservationMessages?.alertSuccesTextBack)}`,
          });
        } else {
          await SweetAlert.fire({
            icon: "error",
            text: intl.formatMessage(reservationMessages?.sendUnmaskedReservationEmailError),
            cancelButtonText: `${intl.formatMessage(reservationMessages?.alertSuccesTextBack)}`,
          });
        }
      } finally {
        setSendEmailLoading(false);
      }
    }, [props?.row?.bookingId, props?.row?.propertyId]);

    const handleSendQuotation = useCallback(async () => {
      const reservationId: string | undefined = props?.row?.multipleBookingId;
      const reservationPropertyTicker = props.row.propertyId;
      if (!reservationId || !reservationPropertyTicker || !userProfile?.email) return;

      const email: string | undefined = props?.row?.affiliate;
      const locale: string | undefined = props?.row?.locale;
      const recipients: string[] =
        email && userProfile?.email !== email ? [email, userProfile.email] : [userProfile.email];
      try {
        setSendEmailLoading(true);
        const propertyEntity = await getEntity();
        const propertyName = propertyEntity.translationsList.find(it => it.locale === locale)?.name;
        const subject = `${intl.formatMessage(
          reservationMessages.sendQuotationSubject
        )} ${reservationId}${propertyName ? ", " + propertyName : ""}`;
        const token =
          propertyTicker !== reservationPropertyTicker
            ? await getPropertyRpt(reservationPropertyTicker, keycloakAuthz)
            : authorizer.getRpt() || "";
        await reservationApi.sendQuotationEmail(
          reservationPropertyTicker,
          reservationId,
          recipients,
          subject,
          token,
          locale
        );
        await SweetAlert.fire({
          icon: "info",
          text: intl.formatMessage(reservationMessages?.sendQuotationEmailSuccess, {
            email: userProfile?.email,
          }),
          cancelButtonText: `${intl.formatMessage(reservationMessages?.alertSuccesTextBack)}`,
        });
      } catch (e) {
        await SweetAlert.fire({
          icon: "error",
          text: intl.formatMessage(reservationMessages?.sendQuotationEmailError),
          cancelButtonText: `${intl.formatMessage(reservationMessages?.alertSuccesTextBack)}`,
        });
      } finally {
        setSendEmailLoading(false);
      }
    }, [
      props.row?.affiliate,
      props.row?.locale,
      props.row?.multipleBookingId,
      props.row?.propertyId,
    ]);

    const handleConfirmQuotation = useCallback(
      async (reload: () => void, propertyId?: string, bookingId?: string, paymentType?: string) => {
        SweetAlert.fire({
          icon: "question",
          showConfirmButton: false,
          showCancelButton: false,
          allowOutsideClick: true,
          didOpen: () => {
            SweetAlert.showLoading();
          },
        }).then(() => {});

        if (!propertyId || !bookingId || !paymentType) {
          SweetAlert.showValidationMessage(`Request failed`);
          SweetAlert.hideLoading();
          return;
        }

        try {
          const reservation = await api.getReservation(bookingId);
          SweetAlert.close();

          await SweetAlert.fire({
            icon: "question",
            showConfirmButton: true,
            showCancelButton: true,
            title: intl.formatMessage(reservationMessages.confirmQuotation),
            text: intl.formatMessage(reservationMessages.confirmQuotationDescription, {
              rooms: reservation.roomReservations.length,
              bookingId,
            }),
            confirmButtonColor: APP_COLORS.primary,
            cancelButtonText: intl.formatMessage(reservationMessages.cancel),
            preConfirm: async () => {
              SweetAlert.showLoading();
              await reservationApi
                .confirmReservation(bookingId, propertyId, paymentType)
                .catch(() => {
                  SweetAlert.showValidationMessage(`Request failed`);
                });
              SweetAlert.hideLoading();
            },
          }).then(async result => {
            if (result.isConfirmed) {
              reload();
            }
          });
        } catch (error) {
          SweetAlert.showValidationMessage(`Request failed`);
          SweetAlert.hideLoading();
        }
      },
      [api]
    );

    return (
      <Table.Cell {...props.rest}>
        {/*Detail*/}
        <OverlayTrigger {...tooltipConfig} overlay={detailTooltip}>
          <Button {...buttonConfig} onClick={() => handleDetail(props.row)}>
            <FontAwesomeIcon icon={faFileAlt} />
          </Button>
        </OverlayTrigger>
        {/*Masked data*/}
        {!showPendingQuotations && authorizer.canExecute(authorizeActions.ExportMaskedData) && (
          <OverlayTrigger {...tooltipConfig} overlay={SendUnmaskedTooltip}>
            <Button
              {...buttonConfig}
              onClick={handleSendUnmaskedReservation}
              disabled={sendEmailLoading}>
              <FontAwesomeIcon icon={faPaperPlane} />
            </Button>
          </OverlayTrigger>
        )}
        {/*Send quotation*/}
        {showPendingQuotations && (
          <OverlayTrigger {...tooltipConfig} overlay={SendQuotationTooltip}>
            <Button {...buttonConfig} onClick={handleSendQuotation} disabled={sendEmailLoading}>
              <FontAwesomeIcon icon={faPaperPlane} />
            </Button>
          </OverlayTrigger>
        )}
        {/*Historical*/}
        {showHistoricalReservations && (
          <OverlayTrigger {...tooltipConfig} overlay={historicalTooltip}>
            <Button
              {...buttonConfig}
              onClick={() => {
                history.push(
                  `${urlNoTicker}/${props?.row?.propertyId}/historical/${props?.row?.bookingId}`
                );
              }}>
              <FontAwesomeIcon icon={faCalendar} />
            </Button>
          </OverlayTrigger>
        )}
        {/*Edit*/}
        {canEdit && (
          <OverlayTrigger {...tooltipConfig} overlay={editTooltip}>
            <Button
              {...buttonConfig}
              style={{ opacity: delayEdit ? 0.6 : 1 }}
              onClick={() => {
                // In case the screen was left open we recalculate the delay again
                const minutesSinceCreation = calculateElapsedMinutesSinceCreation(
                  props?.row?.bookingDate
                );
                if (minutesSinceCreation < 5) {
                  return SweetAlert.fire({
                    icon: "info",
                    html: `<p>${intl.formatMessage(reservationMessages.delayedEdit)}</p>`,
                  });
                }

                history.push(
                  `${urlNoTicker}/${props?.row?.propertyId}/reservationModification/${props?.row?.bookingId}`
                );
              }}>
              <FontAwesomeIcon icon={faPencilAlt} />
            </Button>
          </OverlayTrigger>
        )}
        {/*Cancel*/}
        {(props?.row?.paymentStatus === PAYMENT_STATUS.PENDING ||
          props?.row?.status === RERSERVATION_STATUS.OK) &&
          canEdit && (
            <OverlayTrigger {...tooltipConfig} overlay={cancelTooltip}>
              <Button
                {...buttonConfig}
                onClick={() => {
                  handleCancelReservation(
                    props?.row?.propertyId,
                    props?.row?.bookingId,
                    props.onReloadList,
                    `${props?.row?.customer?.lastName}, ${props?.row?.customer?.name}`,
                    props?.row?.multipleBookingId
                  );
                }}>
                <FontAwesomeIcon icon={faTimesCircle} />
              </Button>
            </OverlayTrigger>
          )}
        {/*Confirm*/}
        {canConfirm && (
          <OverlayTrigger {...tooltipConfig} overlay={confirmOverlay}>
            <Button
              {...buttonConfig}
              onClick={() =>
                handleConfirmReservation(
                  props?.row?.propertyId,
                  props?.row?.bookingId,
                  props?.row?.paymentType,
                  props.onReloadList
                )
              }>
              <FontAwesomeIcon icon={confirmIcon} />
            </Button>
          </OverlayTrigger>
        )}
        {/*Confirm quotation*/}
        {showPendingQuotations && canConfirm && (
          <OverlayTrigger {...tooltipConfig} overlay={ConfirmQuotationTooltip}>
            <span className="w-[10px] h-[10px]">
              <Button
                {...buttonConfig}
                onClick={() =>
                  handleConfirmQuotation(
                    props.onReloadList,
                    props?.row?.propertyId,
                    props?.row?.multipleBookingId,
                    props?.row?.paymentType
                  )
                }>
                <ActivateListIcon />
              </Button>
            </span>
          </OverlayTrigger>
        )}
        {/*Check CC*/}
        {showCCButton && (
          <OverlayTrigger {...tooltipConfig} overlay={showCCTooltip}>
            <Button
              {...buttonConfig}
              disabled={isLoadingCC}
              onClick={() =>
                handleCheckCC(
                  props?.row?.multipleBookingId,
                  props?.row?.propertyId,
                  props?.row?.paymentType
                )
              }>
              {isLoadingCC ? (
                <StyledSpinner className="fa fa-spinner fa-spin" />
              ) : (
                <FontAwesomeIcon icon={faCreditCard} />
              )}
            </Button>
          </OverlayTrigger>
        )}

        {isNoShowAvailable(
          props?.row?.paymentStatus,
          props?.row?.status,
          props?.row?.checkout,
          multiProperty
        ) && (
          <OverlayTrigger
            {...tooltipConfig}
            overlay={
              props?.row?.noShow.toUpperCase() === NO_SHOW_STATUS.NO_SHOW
                ? showTooltip
                : noShowTooltip
            }>
            <Button
              {...buttonConfig}
              onClick={() =>
                handleNoShowConfirmation(
                  propertyTicker,
                  props?.row?.noShow.toUpperCase(),
                  props?.row?.bookingId,
                  props.onReloadList
                )
              }>
              {props?.row?.noShow.toUpperCase() === NO_SHOW_STATUS.NO_SHOW ? (
                <FontAwesomeIcon icon={faUserCheck} />
              ) : (
                <FontAwesomeIcon icon={faUserXmark} />
              )}
            </Button>
          </OverlayTrigger>
        )}
        {/*AutomaticCharges*/}
        {hasAutomaticCharges && (
          <OverlayTrigger {...tooltipConfig} overlay={automaticChargeTooltip}>
            <Button
              {...buttonConfig}
              onClick={() => goToAutomaticCharges(props?.row?.propertyId, props?.row?.bookingId)}>
              <span
                style={{
                  fontSize: "15px",
                  marginTop: "5px",
                }}
                className="material-symbols-outlined">
                credit_card_clock
              </span>
            </Button>
          </OverlayTrigger>
        )}
      </Table.Cell>
    );
  }

  const customRow = useCallback(
    (rows: Reservation[] | undefined = []) => ({
      row,
      ...restProps
    }: ReactGrid.Table.DataRowProps) => {
      return <CustomStyledTableRow {...restProps} row={row} index={rows.indexOf(row)} />;
    },
    []
  );

  const onBatchCancellation = useCallback(() => {
    history.push(`batch-reservation-cancellation`);
  }, [history]);

  const additionalHeaderContent = (
    <div>
      <LegendRow>
        <span>
          <FormattedMessage {...reservationMessages.legendDiffered} />:
        </span>
        {/*    margin-top: -3px;*/}
        <ColorSquare color="info">■</ColorSquare>
        <span>
          <FormattedMessage {...reservationMessages.legendAwaiting} />
        </span>
        <ColorSquare color={"purple"}>■</ColorSquare>
        <span>
          <FormattedMessage {...reservationMessages.legendTimedOut} />
        </span>
      </LegendRow>
      <LegendRow>
        <span>
          <FormattedMessage {...reservationMessages.legendOnline} />:
        </span>
        <ColorSquare color={"success"}>■</ColorSquare>{" "}
        <span>
          <FormattedMessage {...reservationMessages.legendSuccess} />
        </span>
        <ColorSquare color={"danger"}>■</ColorSquare>
        <span>
          <FormattedMessage {...reservationMessages.legendFailed} />
        </span>
        <ColorSquare color={"warning"}>■</ColorSquare>{" "}
        <span>
          <FormattedMessage {...reservationMessages.legendAbandoned} />
        </span>
      </LegendRow>
    </div>
  );

  const actions = useCallback(() => {
    const enableAutomaticCharges =
      propertyConfiguration && propertyConfiguration.enableAutomaticCharges === "1" ? (
        <ExportButton
          disabled={exportMaskedLoading}
          onClick={handleGoToAutomaticCharges}
          variant="primary">
          <FormattedMessage {...reservationMessages.automaticCharges} />
        </ExportButton>
      ) : (
        <></>
      );

    const exportButton = (
      <>
        <ExportButton disabled={exportMaskedLoading} onClick={onExportMasked} variant="primary">
          {exportMaskedLoading ? (
            <FormattedMessage {...reservationCrudMessages.loading} />
          ) : (
            <FormattedMessage {...reservationCrudMessages.export} />
          )}
        </ExportButton>
      </>
    );

    return !email ? (
      <>
        {exportButton}
        {enableAutomaticCharges}
      </>
    ) : (
      <>
        <InsertReservationCallCenter
          propertyTicker={propertyTicker}
          email={email}
          isCallCenterUser={isCallCenterUser}
          engineURL={config.engineBaseUrl}
        />
        {exportButton}
        {enableAutomaticCharges}
      </>
    );
  }, [
    exportMaskedLoading,
    onExportMasked,
    email,
    propertyTicker,
    isCallCenterUser,
    config.engineBaseUrl,
    propertyConfiguration,
    handleGoToAutomaticCharges,
  ]);

  // not using translations for managerName because filter storage won't work on language change
  return (
    <CrudList<Reservation, {}>
      actions={actions}
      messages={showPendingQuotations ? quotationsCrudMessages : reservationCrudMessages}
      rowActions={props => <TableRowActions {...props} />}
      columns={formattedColumns}
      onGetAll={onGetAll}
      onExport={onExportUnmasked}
      paging={true}
      columnVisibility={true}
      columnVisibilityExtensions={columnVisibilityExtensions}
      pageSizes={pageSizes}
      defaultPageSize={defaultPageSize}
      initialHiddenColumnNames={initialHiddenColumnNames}
      filters={tableFilters}
      managerName={"reservation"}
      customRow={customRow}
      additionalHeaderContent={additionalHeaderContent}
      onBatchCancellation={onBatchCancellation}
      showBatchCancellation={propertyConfiguration?.showBatchCancellation === "1"}
      fixedActions={true}
      onExportText={intl.formatMessage(reservationMessages.exportUnmaskedReservationsBtn)}
      hideExportButton={
        !authorizer.canExecute(authorizeActions.ExportMaskedData) ||
        !authorizer.canExecute(authorizeActions.SendUnmaskedData)
      }
      forceLoad={loadRetryAttempt}
    />
  );
}
